import ComparisonGrowthSelector from '@components/comparison-growth-selector/ComparisonGrowthSelector';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import ProductsActionBar from '@components/products-action-bar/ProductsActionBar';
import { useProductsModelCompare } from '@components/products-compare-modal/ProductsModelCompareContext';
import { ProductsCompareLocationState } from '@components/products-compare-modal/Types';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import ProductsTable from '@components/top-selling-products/ProductsTable';
import SellingProductsHeaderIcon from '@components/top-selling-products/header-icon/SellingProductsHeaderIcon';
import { CreateProductLeaderboardGroupModal } from '@components/top-selling-products/model-group/create-model-group/CreateProductLeaderboardGroupModal';
import { DeleteProductLeaderboardModalGroup } from '@components/top-selling-products/model-group/delete-model-group/DeleteProductLeaderboardGroupModal';
import ProductAnalyzerGroupProvider from '@components/top-selling-products/product-table-grouping/utils/ProductAnalyzerGroupProvider';
import { DisplayOptions } from '@components/top-selling-products/products-table-filter/Types';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { containers, navigation } from '@ecosystem/newron-design-system';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { ViewType } from '@hooks/export/Model';
import {
  ColumnHeader,
  ComponentName,
  DefaultSortingOptionsForProducts,
  getBarChartKpiDisplayName,
  ItemFeaturesResp,
  KpiOptions,
  LeaderboardKpi,
  ProductAnalyzerGroupKpi,
  ProductsParam,
  SaleType,
  TopSellingProductsChartId,
} from '@models/Products';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { NoResult } from '@pages/NoResult';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { getProductsLeaderboard } from '@services/ProductsLeaderboard.service';
import { getItemFeatures } from '@services/productTab/ProductsItemFeatures.service';
import { actionCreatorsHitlistsRetailerClient, actionCreatorsProducts } from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { getAppliedFiltersFromPagesConfig } from '@utils/FilterUtils';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import { checkPeriodExist, getCorrespondingLatestPeriod, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { BASE_RETAIL_PATH, RETAIL_ALLOWED_PATH } from '@utils/UrlUtils';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { useProductAnalyzerGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { ChartElementName, ChartElementType } from '@utils/page-analytics/model/ChartInteractionTracking';
import {
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  TOGGLE_CLICKED_TITLE,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import {
  TABLE_CLICKED_TRACKING_TITLE,
  tableClickTrackingFunction,
  TableClickTrackingParam,
} from '@utils/page-analytics/model/TableClickTracking';
import { PagesConfig, usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  CountryFilterPair,
  FactRevenue,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  SubSection,
} from '@utils/pageConfig/Types';
import { constructProductsParam } from '@utils/serviceUtils/RequestFormatter';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { capitalize, isEmpty } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import {
  applyTimeFramePickerProductsCallback,
  countriesToQuery,
  getCurrentRetailerClientState,
  isInvalidPG,
  ProductsParamArgumentObject,
  ProductType,
  updateSortingField,
} from '../Product';
import * as styles from './Leaderboard.module.scss';
import { TableTrackingParams } from './LeaderboardTypes';
import { useTopSellingExportExcelCallback, UseTopSellingExportExcelParam } from './UseTopSellingExportExcelCallback';
import { ShareProductLeaderboardGroupModal } from '@components/top-selling-products/model-group/share-model-group/ShareProductLeaderboardGroupModal';
import { GeneralViewState } from '@models/SavedViews';
import ProductsTableFilter from '@components/top-selling-products/products-table-filter/ProductsTableFilter';
import { ChartPickers } from '@components/chartpickers/ChartPickers';
import { RI_PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS_DOUBLE_BAR_CHART } from '@pages/Utilities';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import { getBarChartDropwdownKpi } from '@data-visualization/products-leaderboard-bar-chart/ProductsChartConfig';
import { RetailerClient } from '@models/RetailerClient';
import { getCountryFilterPairsByTimeFrame } from '@utils/CountryUtils';
import ProductsSegments from '@components/products-segments/ProductsSegments';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { ProductsLeaderboardDoubleBarChartInsight } from '@components/collections/products-leaderboard-double-bar-chart/Model';
import { ColumnConfig } from '@components/design/migrated/Table';
import ErrorResultMessage from '@pages/ErrorResultMessage';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

interface Props {
  filters: MainFilters;
  timeFrame: Periodicity;
  period: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  setTimeFrame: Dispatch<SetStateAction<Periodicity>>;
  setPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  setComparisonPeriod: Dispatch<SetStateAction<RelativePeriod | BaseTimePeriod>>;
  selectedProductGroup: Option[] | undefined;
  appliedCountries: Option[];
  timeFrameOnChangeHandler: any;
  pgListByCountry: CountryFilterPair[];
  factRevenue: FactRevenue;
  currentProductType: ProductType;
  isTableView: boolean;
  setIsTableView: Dispatch<SetStateAction<boolean>>;
  selectedDisplayType: DisplayOptions;
  setSelectedDisplayType: Dispatch<SetStateAction<DisplayOptions>>;
  selectedSaleType: SaleType;
  setSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  currentSortingOption: SortingOptions;
  setCurrentSortingOption: Dispatch<SetStateAction<SortingOptions>>;
  isMultibannerChanging: boolean;
  defaultFilters: MainFilters[];
  selectedGrowthType: GrowthTypes;
  setSelectedGrowthTypes: Dispatch<SetStateAction<GrowthTypes>>;
  prodLeaderboardFeatureProductFilters: Filters[];
  productSpecificationFilters: Filters[];
  saveViewFilter: GeneralViewState;
  retailerClients: RetailerClient[];
}

// Reset value from context
export const resetIsRetailerChanged = (pagesConfig: PagesConfig) => {
  pagesConfig.setIsRetailerChanged(false);
};

const Leaderboard: React.FC<Props> = ({
  filters,
  timeFrame,
  period,
  comparisonPeriod,
  setTimeFrame,
  setPeriod,
  setComparisonPeriod,
  selectedProductGroup,
  appliedCountries,
  timeFrameOnChangeHandler,
  pgListByCountry,
  factRevenue,
  currentProductType,
  isTableView,
  setIsTableView,
  selectedDisplayType,
  setSelectedDisplayType,
  selectedSaleType,
  setSelectedSaleType,
  currentSortingOption,
  setCurrentSortingOption,
  isMultibannerChanging,
  defaultFilters,
  selectedGrowthType,
  setSelectedGrowthTypes,
  prodLeaderboardFeatureProductFilters,
  productSpecificationFilters,
  saveViewFilter,
  retailerClients,
}) => {
  const navigate = useNavigate();

  const initialState: LeaderboardKpi[] = [];
  const defaultPageNumber = 0;
  const defaultPageSize = 50;
  const pagesConfig = usePageConfig();
  const [leaderboardUpdate, setLeaderboardUpdate] = useState<boolean>(false);
  const [isFetchingApi, setIsFetchingApi] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [itemFeatures, setItemFeatures] = useState<ItemFeaturesResp[]>();

  const [appliedProductGroups, setAppliedProductGroups] = useState<any>(
    getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT),
  );

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RI_PRODUCT);

  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [leaderboardContent, setLeaderboardContent] = useState<LeaderboardKpi[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(defaultPageNumber);
  const [totalPage, setTotalPage] = useState<number>(5);
  const [totalElements, setTotalElements] = useState<number>(0);

  const [productsParam, setProductsParam] = useState<ProductsParam>();

  // Redux
  const dispatch = useDispatch();
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const hitlistsRetailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);
  const sellerInsightsRetailerClientState = useSelector((state: any) => state.sellerInsightsRetailerClient);

  const currentRetailerClientState = getCurrentRetailerClientState(
    currentProductType,
    retailerClientState,
    hitlistsRetailerClientState,
    sellerInsightsRetailerClientState,
  );

  const mainFilterStateProducts = useSelector((state: any) => state.mainFiltersProducts);
  const { track } = useAnalytics();
  const [isViewTypeChanged, setIsViewTypeChanged] = useState<boolean>(false);
  const sideFiltersState: SideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const { clearSelectedModels, selectedModels } = useProductsModelCompare();
  const [productAnalyzerGroupKpi, setProductAnalyzerGroupKpi] = useState<ProductAnalyzerGroupKpi[]>([]);

  const [isPgListByCountryUpdated, setIsPgListByCountryUpdated] = useState<boolean>(true);

  //disable export button while the data is still loading/inline alert is displaying
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(true);
  const [isExportFeatureButtonDisabled, setIsExportFeatureButtonDisabled] = useState<boolean>(true);
  const createViewState = useCallback(() => saveViewFilter, [saveViewFilter]);

  // Icon Click Tracking Function
  const handleElementClickTracking = (title: string) => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: hitlistsRetailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      chartName: ComponentName.PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS,
      optionChosen: title,
      elementName: TOGGLE_CLICKED_TITLE,
      access: pagesConfig.access,
      subSection: SubSection.PRODUCT_LEADERBOARD,
    };
    pageElementClickTrackingFunction(params);
  };

  const handleViewChange = (view: ViewType) => {
    handleElementClickTracking(view);
    setIsTableView(view === ViewType.Table);
    if (view === ViewType.Table) setIsViewTypeChanged(true);
  };

  const handlePageChange = async (pageNumber: number) => {
    pageNumber--;

    setIsLoading(true);
    setIsApiError(false);
    if (!leaderboardUpdate && pagesConfig.sortingOptionsProducts === currentSortingOption) {
      const productsParamObject: ProductsParamArgumentObject = {
        productType: effectiveProductType,
        pageNumber: pageNumber,
        productGroupParam: selectedProductGroup ? selectedProductGroup[0] : selectedProductGroup,
        pagesConfig: pagesConfig,
        period: period,
        mainFilterStateProducts: mainFilterStateProducts,
        retailerClientState: currentRetailerClientState,
        countriesToQuery: countriesToQuery(pagesConfig, appliedCountries, effectiveProductType),
        timeFrame: mainFilterStateProducts.timeFrame,
        defaultPageSize: defaultPageSize,
        selectedSaleType: selectedSaleType,
        sortingKpi: undefined,
        isMarket: undefined,
        growthType: selectedGrowthType,
        comparisonPeriod: comparisonPeriod,
        productGrouping: allGroupsForApiCalls,
      };
      const productsParameters = constructProductsParam(productsParamObject);
      if (!productsParameters) {
        return;
      }
      const leaderboardData = await getProductsLeaderboard(productsParameters);

      // Getting item id from leaderboard api
      const itemIds = leaderboardData.content.map((data: any) => {
        return data.item.itemId;
      });

      const itemFeaturesData = await getItemFeatures(
        currentRetailerClientState.clientId,
        effectiveProductType,
        countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0].id,
        timeFrame,
        selectedProductGroup ? selectedProductGroup[0] : selectedProductGroup,
        itemIds,
      );

      setIsLoading(false);
      setLeaderboardContent(leaderboardData.content);
      setProductAnalyzerGroupKpi(groupingKpiList(leaderboardData.groupingKpi));
      setItemFeatures(itemFeaturesData);
      setCurrentPage(pageNumber);
    }
  };

  //Grouping state
  const groupContext: GroupContext = useMemo(() => {
    //direct take from page config to prevent race condition
    const productGroupApplied = getAppliedFiltersFromPagesConfig(
      pagesConfig,
      FilterKeys.PRODUCT_GROUP,
      PageType.RI_PRODUCT,
    );
    const countryApplied = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);

    return {
      client: hitlistsRetailerClientState.clientName?.trim()
        ? hitlistsRetailerClientState.clientName
        : hitlistsRetailerClientState.clientId.toString(),
      country: countryApplied === undefined ? '' : countryApplied[0].value,
      productGroup: productGroupApplied === undefined ? '' : productGroupApplied[0].value,
    };
  }, [pagesConfig.appliedFilters]);

  const { allGroupsForApiCalls, getProductAnalyzerGroup, getNewSortValues } =
    useProductAnalyzerGroupState(groupContext);

  const groupingKpiList = (groupingkpi: ProductAnalyzerGroupKpi[] | undefined) => {
    return groupingkpi ?? [];
  };
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const isUpdateGroup = useRef(false);

  const setDataStateToDefault: () => void = () => {
    setIsExportButtonDisabled(true);
    setIsExportFeatureButtonDisabled(true);
    setLeaderboardUpdate(false);
    setTotalPage(0);
    setCurrentPage(defaultPageNumber);
    setLeaderboardContent(initialState);
    setIsApiError(false);
  };

  /**
   * Extracted out to avoid duplicate code by sonar
   * @param countries
   * @param productGroup
   * @param responseTimeFrame
   * @param responseTimePeriod
   * @returns ProductsParamArgumentObject
   */
  const constructProductsParamObject = (
    countries: Option[],
    productGroup: Option[],
    responseTimeFrame: Periodicity,
    responseTimePeriod: BaseTimePeriod,
  ) => {
    const productsParamObject: ProductsParamArgumentObject = {
      productType: effectiveProductType,
      pageNumber: defaultPageNumber,
      productGroupParam: productGroup[0],
      pagesConfig: pagesConfig,
      period: responseTimePeriod,
      mainFilterStateProducts: mainFilterStateProducts,
      retailerClientState: currentRetailerClientState,
      countriesToQuery: countriesToQuery(pagesConfig, countries, effectiveProductType),
      timeFrame: responseTimeFrame,
      defaultPageSize: defaultPageSize,
      selectedSaleType: selectedSaleType,
      sortingKpi: undefined,
      isMarket: undefined,
      growthType: selectedGrowthType,
      comparisonPeriod: comparisonPeriod,
      productGrouping: allGroupsForApiCalls,
    };
    return productsParamObject;
  };

  /*
   * calls top selling products api and gets it response
   */
  const loadProductsService = async (responseTimeFrame: Periodicity, responseTimePeriod: BaseTimePeriod) => {
    // we get the country & pg from pagesConfig.applied filters to avoid situations where appliedFilters is not updated yet
    const countries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);
    const productGroup = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT);
    /**
     * this checking had to put in here to ensure the grouping data is retrieved
     * if is this put in earlier block, the getGroup is not called yet.
     */
    if (getProductAnalyzerGroup.isLoading) {
      return;
    }
    const skipLoad = !countries || !productGroup || !effectiveProductType;
    if (skipLoad) {
      setIsLoading(false);
      return;
    }
    setAppliedProductGroups(productGroup);

    if (isInvalidPG(responseTimeFrame, countries, productGroup, pgListByCountry, pagesConfig)) {
      setDataStateToDefault();
      setIsLoading(false);
      return;
    }

    setIsFetchingApi(true);
    setIsLoading(true);
    await (async () => {
      try {
        const productsParameter = constructProductsParam(
          constructProductsParamObject(countries, productGroup, responseTimeFrame, responseTimePeriod),
        );
        if (!productsParameter) {
          setIsExportButtonDisabled(true);
          return;
        }
        const leaderboardData = await getProductsLeaderboard(productsParameter);
        setProductsParam(productsParameter);
        if (!leaderboardData.content) {
          setLeaderboardUpdate(false);
          setIsExportButtonDisabled(true);
        } else {
          // Getting item id from leaderboard api
          const itemIds = leaderboardData.content.map(({ item: { itemId } }) => itemId);

          const itemFeaturesData = await getItemFeatures(
            currentRetailerClientState.clientId,
            effectiveProductType,
            countriesToQuery(pagesConfig, countries, effectiveProductType)[0].id,
            timeFrame,
            productGroup[0],
            itemIds,
          );
          if (itemFeaturesData.length > 0) {
            setIsExportFeatureButtonDisabled(false);
          }
          setItemFeatures(itemFeaturesData);
          setCurrentPage(defaultPageNumber);
          setTotalPage(leaderboardData.pageable.totalPage);
          setTotalElements(leaderboardData.pageable.totalElements);
          setLeaderboardContent(leaderboardData.content);
          setCurrentSortingOption(pagesConfig.sortingOptionsProducts);
          setProductAnalyzerGroupKpi(groupingKpiList(leaderboardData.groupingKpi));
          setIsExportButtonDisabled(false);
        }
      } catch (err: any) {
        console.error(err);
        setDataStateToDefault();
        saveErrorLog(err, 'Products Leaderboard - Unable to get products leaderboard api response.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: currentRetailerClientState.clientId,
            productGroup: selectedProductGroup,
            period: responseTimePeriod,
            comparisonPeriod: comparisonPeriod,
          },
        });
        setApiErrorState(err, setIsApiError);
      } finally {
        setIsFetchingApi(false);
        // Listen and used to prevent multiple api calling , and set false sequence is important
        pagesConfig.setIsRetailerChanged(false);
        pagesConfig.setIsCountryChanged(false);
        setIsLoading(false);
        isUpdateGroup.current = true;
      }
    })();
  };

  // Should only call amplitude tracking once after the page is rendered
  useEffect(() => {
    // doDefaultPageAnalytics(track, page, 'Products Leaderboard');
    //Update page viewing
    updateHitlistsRetailerClient({
      ...hitlistsRetailerClientState,
      pageViewing: PageName.RI_PRODUCT,
    });

    //Start Page View Amplitude Tracking
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: hitlistsRetailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      access: pagesConfig.access,
      subSection: SubSection.PRODUCT_LEADERBOARD,
    };

    pageViewTrackingFunction(trackingParam);
    //End Page View Amplitude Tracking
  }, []);

  /**
   * Inidicate that the pgListByCountry prop has been updated as it is updated asynchronously via
   * an API call.
   *
   * This is useful during data API calls, where pgListByCountry is used to check whether the
   * applied product group is valid or not, which prevents any invalid API calls that potentially
   * results in a 403 error.
   */
  useEffect(() => {
    if (!isPgListByCountryUpdated) {
      setIsPgListByCountryUpdated(true);
    }
  }, [pgListByCountry]);

  useEffect(() => {
    if (leaderboardContent) {
      setLeaderboardUpdate(false);
    }
  }, [leaderboardContent]);

  /*
   * resetLeaderboard state when filter changes
   */
  useEffect(() => {
    isUpdateGroup.current = false;
    setIsLoading(true);
    setTotalPage(0);
    setCurrentPage(defaultPageNumber);
    setLeaderboardContent(initialState);
    setIsApiError(false);
  }, [timeFrame, period.value, comparisonPeriod, pagesConfig.appliedFilters]);

  /*
   * calls API and updates the leaderboard table when new filters are applied
   */
  useEffect(() => {
    if (isMultibannerChanging || !isPgListByCountryUpdated) {
      return;
    }

    // Allows us to reinvoke the data API call once pgListByCoutry is updated
    if (pgListByCountry.length <= 0) {
      setIsPgListByCountryUpdated(false);
      return;
    }

    if (!isFetchingApi) {
      const responseTimeFrame = checkPeriodExist(
        defaultFilters,
        countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0]?.id,
        timeFrame,
      );
      if (!responseTimeFrame) {
        setIsLoading(false);
        return;
      }

      const responsePeriod = getCorrespondingLatestPeriod(defaultFilters, pagesConfig, responseTimeFrame, period);

      loadProductsService(responseTimeFrame, responsePeriod);
    } else {
      resetIsRetailerChanged(pagesConfig);
    }
    clearSelectedModels();
  }, [
    timeFrame,
    period.value,
    comparisonPeriod,
    pagesConfig.isRetailerChanged,
    pagesConfig.appliedFilters,
    getProductAnalyzerGroup.isLoading,
    isMultibannerChanging,
  ]);

  const loadProductsWithGroupService = async (responseTimeFrame: Periodicity, responseTimePeriod: BaseTimePeriod) => {
    // we get the country & pg from pagesConfig.applied filters to avoid situations where appliedFilters is not updated yet
    const countries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);
    const productGroup = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT);
    const skipLoad = !countries || !productGroup || !effectiveProductType;
    if (skipLoad || getProductAnalyzerGroup.isLoading || isFetchingApi) {
      return;
    }
    setAppliedProductGroups(productGroup);

    if (isInvalidPG(responseTimeFrame, countries, productGroup, pgListByCountry, pagesConfig)) {
      return;
    }
    await (async () => {
      try {
        const productsParameter = constructProductsParam(
          constructProductsParamObject(countries, productGroup, responseTimeFrame, responseTimePeriod),
        );
        if (!productsParameter) {
          setIsExportButtonDisabled(true);
          return;
        }
        if (!isUpdateGroup.current) {
          isUpdateGroup.current = true;
          return;
        }
        setIsFetchingApi(true);
        const leaderboardData = await getProductsLeaderboard(productsParameter);
        setProductsParam(productsParameter);
        if (!leaderboardData.content) {
          setLeaderboardUpdate(false);
          setIsExportButtonDisabled(true);
        } else {
          setProductAnalyzerGroupKpi(groupingKpiList(leaderboardData.groupingKpi));
          setIsExportButtonDisabled(false);
        }
      } catch (err: any) {
        console.error(err);
        saveErrorLog(err, 'Products Leaderboard - Unable to get products leaderboard api response.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: currentRetailerClientState.clientId,
            productGroup: selectedProductGroup,
            period: responseTimePeriod,
            comparisonPeriod: comparisonPeriod,
          },
        });
      } finally {
        setIsFetchingApi(false);
        // Listen and used to prevent multiple api calling , and set false sequence is important
        pagesConfig.setIsRetailerChanged(false);
        pagesConfig.setIsCountryChanged(false);
      }
    })();
  };

  /**
   * UseEffect to update grouping data
   * This is use for just rerender the goruped data instead the whole leaderboard data
   */
  useEffect(() => {
    if (isMultibannerChanging || !isPgListByCountryUpdated) {
      return;
    }

    // Allows us to reinvoke the data API call once pgListByCoutry is updated
    if (pgListByCountry.length <= 0) {
      setIsPgListByCountryUpdated(false);
      return;
    }

    if (pagesConfig.isRetailerChanged) {
      resetIsRetailerChanged(pagesConfig);
      return;
    }

    const responseTimeFrame = checkPeriodExist(
      defaultFilters,
      countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0]?.id,
      timeFrame,
    );
    if (!responseTimeFrame) {
      setIsLoading(false);
      return;
    }

    const responsePeriod = getCorrespondingLatestPeriod(defaultFilters, pagesConfig, responseTimeFrame, period);

    loadProductsWithGroupService(responseTimeFrame, responsePeriod);
  }, [getProductAnalyzerGroup.data, isMultibannerChanging]);

  /*
   * updates leaderboardUpdate when sortingOptions and saleType are changed
   */
  useEffect(() => {
    const options = pagesConfig.sortingOptionsProducts;

    if (
      currentRetailerClientState.clientId === -1 ||
      !checkPeriodExist(
        defaultFilters,
        countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0].id,
        timeFrame,
      ) ||
      countriesToQuery(pagesConfig, appliedCountries, effectiveProductType).length === 0
    ) {
      return;
    }

    if (options !== currentSortingOption) {
      //Amplitude tracking for products table when sorting is changed
      handleTableClickTracking({
        componentName: ChartElementName.TABLE_SORT,
        elementType: ChartElementType.SORT,
        optionChosen: `${options.label} ${capitalize(options.sortDir)}`,
        column: getBarChartKpiDisplayName(options.headerKey),
      });
      setIsLoading(true);
      setLeaderboardContent(initialState); // Reset product Leaderboard to empty array when sort option is changed
    }

    setLeaderboardUpdate(true);
    clearSelectedModels();
  }, [pagesConfig.sortingOptionsProducts, selectedSaleType, selectedGrowthType]);

  /*
   * calls leaderboard api and gets its response when leaderboardUpdate is updated
   */
  useEffect(() => {
    if (!isMultibannerChanging) {
      if (!leaderboardUpdate || pgListByCountry.length <= 0) {
        return;
      }

      if (isFetchingApi) {
        resetIsRetailerChanged(pagesConfig);
        return;
      }

      const responseTimeFrame = checkPeriodExist(
        defaultFilters,
        countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0]?.id,
        timeFrame,
      );
      if (!responseTimeFrame) {
        setIsLoading(false);
        return;
      }

      const responsePeriod = getCorrespondingLatestPeriod(defaultFilters, pagesConfig, responseTimeFrame, period);

      loadProductsService(responseTimeFrame, responsePeriod);
    }
  }, [leaderboardUpdate, isMultibannerChanging]);

  const pageElementViewParam = useGeneratePageElementViewParam();
  const leaderboardPageElementViewParam = (elementType: ElementType) => () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    subSection: SubSection.PRODUCT_LEADERBOARD,
    componentName: ComponentName.PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS,
    elementType,
    elementKpi: capitalize(selectedSaleType),
    elementSort: currentSortingOption,
    hasData: !isEmpty(leaderboardContent),
    access: pagesConfig.access,
  });

  const { ref: ProductsLeaderboardTableComponent } = useInViewTracking(
    leaderboardPageElementViewParam(ElementType.TABLE),
    { threshold: 0.1, triggerOnce: true },
  );

  const { sorting, updateOptions } = usePersistedSorting({
    tableId: TopSellingProductsChartId,
    defaultSorting: DefaultSortingOptionsForProducts,
  });

  const getKpiOptions = (columnConfig: ColumnConfig[]): KpiOptions[] =>
    columnConfig.map(({ headerKey, name }) => ({
      label: name,
      value: headerKey as ColumnHeader,
    }));
  const kpiData = getBarChartDropwdownKpi(productSpecificationFilters, isPeriodYTDOrMAT(period));
  const kpiDropdownOptions = getKpiOptions(kpiData);

  const selectedKpi = kpiDropdownOptions.find((option) => option.value === sorting.headerKey) || kpiDropdownOptions[0];

  //Export Functionalities for top-selling products
  const useTopSellingParam: UseTopSellingExportExcelParam = {
    productsParam: productsParam,
    mainFilterStateProducts: mainFilterStateProducts,
    leaderboardContent: leaderboardContent,
    pagesConfig: pagesConfig,
    appliedCountries: appliedCountries,
    appliedPg: appliedProductGroups,
    factRevenue: factRevenue,
    timeFrame: timeFrame,
    comparisonPeriod: comparisonPeriod,
    leaderboardUpdate: leaderboardUpdate,
    isTableView: isTableView,
    saleType: selectedSaleType,
    sortBy: currentSortingOption.headerKey,
    sortDirection: currentSortingOption.sortDir,
    sortKey: currentSortingOption.label?.toUpperCase(),
    selectedKpi,
  };
  const { onChartDownload, onExportReady } = useTopSellingExportExcelCallback(
    useTopSellingParam,
    prodLeaderboardFeatureProductFilters,
  );

  const createDoubleBarChartInsightDataFn: CreateInsightDataFn<ProductsLeaderboardDoubleBarChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const countries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);
    const productGroup = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT);
    const responseTimeFrame = checkPeriodExist(
      defaultFilters,
      countriesToQuery(pagesConfig, appliedCountries, effectiveProductType)[0]?.id,
      timeFrame,
    );

    const responsePeriod = getCorrespondingLatestPeriod(defaultFilters, pagesConfig, responseTimeFrame, period);
    const params = constructProductsParam(
      constructProductsParamObject(countries, productGroup, responseTimeFrame, responsePeriod),
    );

    const apiParams = {
      ...params,
      sorting,
      productSpecificationFilters,
      productGroup: [params?.productGroup?.value],
      countries: [params?.countryId],
    };
    return {
      apiParams,
      selectedGrowthType: selectedGrowthType,
      factRevenue: factRevenue,
      selectedKpiOption: selectedKpi,
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName,
    };
  };

  const renderTable = () => {
    const { Pagination } = navigation;
    const content = renderVisualization({
      isLoading: isLoading,
      fetchedApiData: leaderboardContent,
      defaultData: [],
      jsxToRender: (
        <>
          <ProductsTable
            productsLeaderboard={leaderboardContent}
            itemFeatures={itemFeatures}
            selectedDisplayType={selectedDisplayType}
            selectedSaleType={selectedSaleType}
            factRevenue={factRevenue}
            selectedGrowthType={selectedGrowthType}
            setSelectedSaleType={setSelectedSaleType}
            isViewTypeChanged={isViewTypeChanged}
            enableCheckboxes={true}
            prodLeaderboardFeatureProductFilters={prodLeaderboardFeatureProductFilters}
            productAnalyzerGroupKpi={productAnalyzerGroupKpi}
            context={groupContext}
            isLeaderBoard={true}
            productSpecificationFilters={productSpecificationFilters}
          />
          <div className="mt-6" data-testid="pagination">
            <Pagination current={currentPage + 1} total={totalPage} onChange={handlePageChange} />
          </div>
        </>
      ),
      noResultJsx: <div ref={ProductsLeaderboardTableComponent}>{NoResult()}</div>,
      errorMessageJsx: <div ref={ProductsLeaderboardTableComponent}>{ErrorResultMessage()}</div>,
      isError: isApiError,
    });

    return <div ref={ProductsLeaderboardTableComponent}>{content}</div>;
  };

  const renderBarChart = () => {
    return (
      <ProductsSegments
        topSellingProductsContent={leaderboardContent}
        selectedGrowthType={selectedGrowthType}
        factRevenue={factRevenue}
        isLoading={isLoading}
        productSpecificationFilters={productSpecificationFilters}
        sorting={sorting}
        onExportReady={onExportReady}
        isApiError={isApiError}
      />
    );
  };

  const onChangeSelectedGrowthType = (growthType: GrowthTypes) => {
    setSelectedGrowthTypes(growthType);
    updateMainFiltersProducts({
      timeFrame,
      period: mainFilterStateProducts.period,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: growthType,
      country: appliedCountries[0].value,
    });
  };

  const renderFilters = () => {
    return isTableView ? renderTableFilters() : renderDoubleBarchartFilters();
  };

  const renderTableFilters = () => {
    return (
      <ProductsTableFilter
        selectedSaleType={selectedSaleType}
        selectedDisplayType={selectedDisplayType}
        onChangeSelectedSaleType={setSelectedSaleType}
        onChangeDisplayType={setSelectedDisplayType}
        setIsViewTypeChanged={setIsViewTypeChanged}
        isLoading={isLoading}
      />
    );
  };

  const renderDoubleBarchartFilters = () => {
    return (
      <ChartPickers
        sorting={sorting}
        updateSorting={updateOptions}
        kpisConfig={kpiData}
        getDisplayName={getBarChartKpiDisplayName}
        chartName={RI_PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS_DOUBLE_BAR_CHART}
        onKpiSelectHandler={clearSelectedModels}
        onSortHandler={clearSelectedModels}
        factRevenue={factRevenue}
        retailerClientStateKey="hitlistsRetailerClient"
        isLoading={isLoading}
      />
    );
  };

  const renderContent = () => {
    return isTableView ? renderTable() : renderBarChart();
  };

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, timeFrame);

  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePickerProductsCallback({
      appliedPeriodicity: appliedPeriodicity,
      appliedPeriod: appliedPeriod,
      appliedRelativePeriod: appliedRelativePeriod,
      pagesConfig: pagesConfig,
      mainFilterStateProducts: mainFilterStateProducts,
      sideFiltersState: sideFiltersState,
      retailerClientState: hitlistsRetailerClientState,
      track: track,
      setTimeFrame: setTimeFrame,
      setPeriod: setPeriod,
      setRelativePeriod: setComparisonPeriod,
      updateMainFiltersProducts: updateMainFiltersProducts,
    });

    updateSortingField(appliedPeriod, pagesConfig);
  };
  const renderSellingProductsHeaderIcon = () => (
    <SellingProductsHeaderIcon
      isExportButtonDisabled={isDisableExportButton()}
      onChartDownload={onChartDownload}
      isTableView={isTableView}
      saleType={selectedSaleType}
      onChange={handleViewChange}
      createInsightDataFn={createDoubleBarChartInsightDataFn}
      createViewStateFn={createViewState}
    />
  );

  const isDisableExportButton = () => {
    const base =
      selectedDisplayType === DisplayOptions.feature
        ? (isExportButtonDisabled ?? isExportFeatureButtonDisabled)
        : isExportButtonDisabled;

    return isLoading ? true : (base ?? false);
  };

  const { Card } = containers;

  const handleTableClickTracking = (params: TableTrackingParams) => {
    const { componentName, elementType, optionChosen, column } = params;

    if (!isTableView) {
      return;
    }

    const trackingParam: TableClickTrackingParam = {
      eventTitle: TABLE_CLICKED_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      componentName: componentName,
      elementType: elementType,
      optionChosen: optionChosen,
      ...(column && { column: column }),
      access: pagesConfig.access,
    };
    tableClickTrackingFunction(trackingParam);
  };

  //Get brand name and model name from the selected models
  const getItemName = (): (string | undefined)[] => {
    if (!selectedModels || !leaderboardContent) return [];
    return selectedModels
      ?.map((s) => {
        const item = leaderboardContent?.find((m) => m.item?.itemId === s)?.item;
        return item ? `${item?.itemBrand} ${item?.itemName}` : undefined;
      })
      .filter((itemName) => itemName !== undefined);
  };

  const navigateToCompareProduct = () => {
    const itemsAsString = getItemName().filter(Boolean).join(', ');
    handleTableClickTracking({
      componentName: ComponentName.PRODUCT_LEADERBOARD_COMPARE_MODELS_TABLE,
      elementType: ElementType.TABLE,
      optionChosen: itemsAsString,
    });

    const latestPeriod: BaseTimePeriod = getCorrespondingLatestPeriod(defaultFilters, pagesConfig, timeFrame);

    const compareProductsState: ProductsCompareLocationState = {
      timeFrame,
      factRevenue,
      currentProductType,
      selectedModels,
      selectedPeriod: period,
      latestPeriod,
      relativePeriod: comparisonPeriod,
    };

    const path = `/${BASE_RETAIL_PATH}/${RETAIL_ALLOWED_PATH.LEADERBOARD_PRODUCTS_COMPARE}`;
    navigate(path, { state: compareProductsState });
  };

  return (
    <>
      <div className="flex items-end pt-6 pb-4">
        <div className={`${styles.Leaderboard__Controls} grid w-full w-full`}>
          <TimeFramePickerWrapper
            currentPeriodicity={timeFrame}
            currentPeriod={period}
            currentComparison={comparisonPeriod}
            periodOptions={filters.timePeriod}
            applyPickerCallback={applyTimeFramePickerCallback}
            isTimeFramePickerDisabled={isLoading}
            isPeriodOptionsDisabled={false}
            periodFormat={PeriodFormat.LONG}
            enableComparisonPeriodCheck={true}
            isHideComparisonPeriodsPanelPredicate={(periodicity) =>
              [Periodicity.YTD, Periodicity.MAT].includes(periodicity)
            }
            isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
          />
          <ComparisonGrowthSelector
            growthOptions={filters?.comparisonGrowth}
            growthOption={onChangeSelectedGrowthType}
            selectedGrowthOption={selectedGrowthType}
            isDisabled={false}
          />
        </div>
      </div>
      <div className="pb-4">
        <FilterSelectionNote
          selectedPeriodicity={timeFrame}
          selectedPeriod={period}
          comparisonPeriodType={comparisonPeriod}
          periodFormat={PeriodFormat.LONG}
          isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
          scopeMetadata={filteredScopeData[0]?.scopeMetadata}
        />
      </div>
      <div>
        <div className="pt-4">
          <FilterChips pgListByCountry={pgListByCountry} selectedTimeFrame={timeFrame} />
        </div>
      </div>
      <div className="pt-6">
        <ProductAnalyzerGroupProvider>
          <Card>
            <Card.Title title={''}>
              <Card.Content>
                {renderFilters()}
                <Card.Title.Actions>{renderSellingProductsHeaderIcon()}</Card.Title.Actions>
              </Card.Content>
            </Card.Title>
            {renderContent()}
          </Card>
          {!isLoading && isTableView && (
            <>
              <ProductsActionBar
                totalElements={totalElements}
                onClickCompare={() => navigateToCompareProduct()}
                context={groupContext}
                isEditable={isEditable}
                setIsEditable={setIsEditable}
              />
              <CreateProductLeaderboardGroupModal context={groupContext} sortOrder={getNewSortValues} />
              <DeleteProductLeaderboardModalGroup context={groupContext} />
              <ShareProductLeaderboardGroupModal
                context={groupContext}
                saveViewFilter={saveViewFilter}
                retailerClients={retailerClients}
              />
            </>
          )}
        </ProductAnalyzerGroupProvider>
      </div>
    </>
  );
};

export default Leaderboard;
